/* eslint-disable */
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import CommonQueries from './sql/queries';
import { style } from 'd3';
import TableExport from 'tableexport';
import { maskState, setUserMask } from './utils/phiMaskState';
import { isPatientPhotoEnabled } from './utils/patientPhotoGate';

// Rebuild CASL permission rules from ability.js at most once per page load (see
// the created() hook below). Module-scope (not component data) so it's shared
// across every component that uses this global mixin.
let rulesRebuiltThisLoad = false;

export default {
    data () {
       return {
         statusColors: {
            'Not Home. Call Again': 'primary',
            'Call Back, Not Ready': 'danger',
            'Patient Will Call Us' : 'FF6384',
            'Wait. See Notes': 'purple',
            'Couldn\'t Leave Voicemail': 'pink',
            'Left Voicemail' : 'f87979',
            'Left Message with Fam': 'orange',
          },
          featureNotEnabledMsg: `You do not have this feature enabled/paid for yet. Please contact teamio by emailing <a mailto:"nilay@teamio.app">nilay@teamio.app</a> to have this feature enabled`
       }
    },
    
    created: async function () {
      //console.log('common created function called from mixin');
      //this.setErrored(false);
      //this.setMessage('');

      // Only with a site. A signed-out page (the PDF page the form renderer loads, /view-submission-for-pdf) has none, and the call
      // was `where SiteID=''`: an anonymous request to the open /runquery that returned nothing and that the page never used.
      if(this.siteID && (!this.settings || this.settings.length === 0))
         await this.getSettings({siteID: this.siteID});

      if(this.siteID && (!this.employees || this.employees.length === 0)) {
         let query = {
               sqlQuery : CommonQueries.getEmployees(this.siteID),
         }
         await this.getEmployees(query);
      }

      // Rebuild CASL rules from the CURRENT ability.js once per page load,
      // ignoring any (possibly stale) localStorage('rules') cache. The old guard
      // only rebuilt when the cache was EMPTY, so an admin who logged in before a
      // new ability shipped (e.g. chat export) kept stale permissions until they
      // re-logged-in. Rebuilding once per load ties permission freshness to the
      // bundle (a deploy = a reload = a rebuild) without rebuilding on every
      // component's created(). Settings are loaded just above, so settings-
      // dependent rules resolve correctly.
      if (this.user && !rulesRebuiltThisLoad) {
         await this.loadRules({user: this.user, settings: this.settings});
         rulesRebuiltThisLoad = true;
      } else if (this.user && (!this.rules || this.rules.length === 0)) {
         await this.loadRules({user: this.user, settings: this.settings});
      }
      if(this.rules && this.rules.length > 0) {
         this.$ability.update(this.rules);
      }
    },
    
    mounted() {
      //console.log('common mounted function called from mixin');
    },

    methods: {
      ...mapActions('alert', ['setDismissSuccessCountDown', 'setLoading2', 'setLoaded2', 'setErrored', 'setErrored2', 'setMessage', 'setMessage2', 'setLoading', 'setLoaded']),
      ...mapActions('customer', ['getSites', 'getEmployees', 'getSettings', 'loadRules']),

      // Defense-in-depth CASL check for action handlers. Returns true if the
      // current user can perform the action; otherwise shows a toast and
      // returns false. UI guards (v-if="$can(...)") are the primary control
      // — this is the runtime fallback for race conditions (rules refreshed
      // mid-session, FEATURE_PERMISSIONS override applied while a stale page
      // is still mounted) or places a v-if was forgotten. Pattern:
      //
      //   onDeleteClick(id) {
      //     if (!this.assertCan('delete', 'workflow')) return;
      //     // ... rest of handler
      //   }
      //
      // Optional opts.message overrides the default text when you need
      // domain-specific wording.
      assertCan(action, subject, opts) {
        if (this.$can && this.$can(action, subject)) return true;
        const message = (opts && opts.message)
          || "You don't have permission to perform this action. Please contact your administrator if you need access.";
        if (this.$toast && typeof this.$toast.error === 'function') {
          this.$toast.error(message);
        }
        return false;
      },

      /*
      printList1(tableData, columns) {

        console.log(tableData, columns);

        const filteredData = tableData.map((obj) => {
          let newObj = {};
          columns.forEach((key) => {
            if (obj.hasOwnProperty(key)) {
              newObj[key] = obj[key];
            }
          });
          return newObj;
        });

        console.log('filteredData', filteredData);

        // Create a new workbook
        const wb = XLSX.utils.book_new();
          
        // Convert the data to worksheet format
        const ws = XLSX.utils.json_to_sheet(filteredData);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Table Data');

        // Generate the Excel file and trigger download
        XLSX.writeFile(wb, 'teamio-download.xlsx');
      },
      */

      printList(tableData, columns) {

        console.log(tableData, columns);

        // https://tableexport.travismclarke.com/

        /* default charset encoding (UTF-8) */
        TableExport.prototype.charset = "charset=utf-8";

        /* default `filename` property if "id" attribute is unset */
        TableExport.prototype.defaultFilename = "teamio-download";

        /* default class to style buttons when not using Bootstrap or the built-in export buttons, i.e. when (`bootstrap: false` & `exportButtons: true`)  */
        TableExport.prototype.defaultButton = "button-default";

        TableExport.prototype.defaultCaptionClass = "tableexport-caption";

        /* Bootstrap classes used to style and position the export button, i.e. when (`bootstrap: true` & `exportButtons: true`) */
        TableExport.prototype.bootstrapConfig = ["btn", "btn-default", "btn-toolbar"];

        /* row delimeter used in all filetypes */
        TableExport.prototype.rowDel = "\r\n";

        /* CSS selector or selector[] to exclude/remove cells (<td> or <th>) from the exported file(s). */
        TableExport.prototype.ignoreCSS = [".tableexport-ignore", ".other-ignore-class"]

        /* CSS selector or selector[] to replace cells (<td> or <th>) with an empty string in the exported file(s).*/
        TableExport.prototype.emptyCSS = [".tableexport-empty", ".other-empty-class"];

        let formatConfig = {
          /**
           * XLSX (Open XML spreadsheet) file extension configuration
           * @memberof TableExport.prototype
           */
          xlsx: {
              defaultClass: 'xlsx',
              buttonContent: 'Export to xlsx',
              mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              fileExtension: '.xlsx'
          },
          xlsm: {
              defaultClass: 'xlsm',
              buttonContent: 'Export to xlsm',
              mimeType: 'application/vnd.ms-excel.sheet.macroEnabled.main+xml',
              fileExtension: '.xlsm'
          },
          xlsb: {
              defaultClass: 'xlsb',
              buttonContent: 'Export to xlsb',
              mimeType: 'application/vnd.ms-excel.sheet.binary.macroEnabled.main',
              fileExtension: '.xlsb'
          },
          /**
           * XLS (Binary spreadsheet) file extension configuration
           * @memberof TableExport.prototype
           */
          xls: {
              defaultClass: 'xls',
              buttonContent: 'Export to xls',
              separator: '\t',
              mimeType: 'application/vnd.ms-excel',
              fileExtension: '.xls',
              enforceStrictRFC4180: false
          },
          /**
           * CSV (Comma Separated Values) file extension configuration
           * @memberof TableExport.prototype
           */
          csv: {
              defaultClass: 'csv',
              buttonContent: 'Export to csv',
              separator: ',',
              mimeType: 'text/csv',
              fileExtension: '.csv',
              enforceStrictRFC4180: true
          },
          /**
           * TXT (Plain Text) file extension configuration
           * @memberof TableExport.prototype
           */
          txt: {
              defaultClass: 'txt',
              buttonContent: 'Export to txt',
              separator: '  ',
              mimeType: 'text/plain',
              fileExtension: '.txt',
              enforceStrictRFC4180: true
          }
        };

        TableExport.prototype.formatConfig = formatConfig;

        let config = {
          headers: true,                      // (Boolean), display table headers (th or td elements) in the <thead>, (default: true)
          footers: true,                      // (Boolean), display table footers (th or td elements) in the <tfoot>, (default: false)
          formats: ["xlsx", "csv", "txt"],    // (String[]), filetype(s) for the export, (default: ['xlsx', 'csv', 'txt'])
          filename: "id",                     // (id, String), filename for the downloaded file, (default: 'id')
          bootstrap: true,                   // (Boolean), style buttons using bootstrap, (default: true)
          exportButtons: false,                // (Boolean), automatically generate the built-in export buttons for each of the specified formats (default: true)
          position: "bottom",                 // (top, bottom), position of the caption element relative to table, (default: 'bottom')
          ignoreRows: null,                   // (Number, Number[]), row indices to exclude from the exported file(s) (default: null)
          ignoreCols: [0],                   // (Number, Number[]), column indices to exclude from the exported file(s) (default: null)
          trimWhitespace: true,               // (Boolean), remove all leading/trailing newlines, spaces, and tabs from cell text in the exported file(s) (default: false)
          RTL: false,                         // (Boolean), set direction of the worksheet to right-to-left (default: false)
          sheetname: "id"                     // (id, String), sheet name for the exported spreadsheet, (default: 'id')
        }

        let tableElm = document.getElementsByClassName("VueTables__table table");
        console.log('tableElm', tableElm);

        
        /*
        let table = TableExport(tableElm, config);
        let exportData1 = table.getExportData();
        console.log('exportData1', exportData1);        
        */

        var instance = new TableExport(tableElm, config);
        console.log('instance', instance);

        var XLS = instance.CONSTANTS.FORMAT.XLSX;
        var CSV = instance.CONSTANTS.FORMAT.CSV;

        var exportData = instance.getExportData()['tableexport-1'][XLS];
        console.log('exportData', exportData);
        
        //var XLSbutton = document.getElementById('customXLSButton');
        instance.export2file(exportData.data, exportData.mimeType, exportData.filename, exportData.fileExtension);        

      },

      isDueToday(d) {
         if(d === null || d === '')
           return false;
         else
           return moment(d).isSame(moment(), 'day');
       },

       // I have format date in Filters but I can't use that in certain places so I needed this function here
       formatDate(value, timezone) {
        //console.log(`got ${value} with tz= ${timezone} in dateformat`);
        //let fmtStr = timezone && timezone.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        let fmtStr = this.dateFormatStr;

        if(value && value !== null && value !== '')
            return moment(value).format(fmtStr);
        else
            return '';
       },

       formatDateWitTz(value, timezone) {
        let temp = moment(value).tz(timezone);
        //let fmtStr = timezone && timezone.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        let fmtStr = this.dateFormatStr;

        if(temp !== undefined)
          return value === '' || value === null ? '' : temp.format(fmtStr);
       },

       formatDateTimeWitTz(value, timezone) {
        if (!value || value === '') return '';
        
        // MySQL DATETIME values don't store timezone info, so we need to treat them as UTC
        // when they come from the database (since MySQL server typically runs in UTC)
        let temp;
        if (timezone) {
          // Check if it's a MySQL datetime string format (YYYY-MM-DD HH:mm:ss or YYYY-MM-DDTHH:mm:ss)
          // MySQL DATETIME values are stored without timezone, but MySQL server typically runs in UTC
          const isMySQLDateTime = typeof value === 'string' && 
            (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/.test(value) || 
             /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}\.\d{3}/.test(value));
          
          // Check if it's a Date object (which might have been created from MySQL datetime)
          const isDateObject = value instanceof Date;
          
          if (isMySQLDateTime) {
            // MySQL datetime string - parse as UTC (since MySQL server runs in UTC)
            // then convert to the site's timezone
            const cleanValue = value.replace('T', ' ').split('.')[0];
            temp = moment.utc(cleanValue).tz(timezone);
          } else if (isDateObject) {
            // Date object from MySQL - JavaScript Date objects from MySQL drivers
            // are typically in UTC, so parse as UTC and convert to site timezone
            temp = moment.utc(value).tz(timezone);
          } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            // Other date string format - assume UTC and convert to site timezone
            temp = moment.utc(value).tz(timezone);
          } else {
            // Fallback: try parsing and converting to site timezone
            // This handles ISO strings with timezone info, timestamps, etc.
            temp = moment(value).tz(timezone);
          }
        } else {
          // No timezone provided, parse without conversion
          temp = moment(value);
        }
        
        let fmtStr = this.dateFormatStr || 'MM/DD/YYYY';
        fmtStr = `${fmtStr} h:mm a`;

        if(temp !== undefined && temp.isValid())
          return temp.format(fmtStr);
        else
          return '';
       },

       /* ── DEMO MASK: these six are now PASS-THROUGHS. ────────────────────────
        * They used to do the masking themselves ("*****, Bibi"), and ~106 call
        * sites across 14 files still call them. Masking now happens at the axios
        * boundary (src/plugins/phiMaskInterceptor.js), so by the time a template
        * calls formatName() the value it holds is ALREADY the fake name.
        *
        * They must NOT mask again — `formatName('Alvarez, Maria')` under the old
        * regex would render "*****, Maria" and undo the whole point of the
        * rewrite. They are kept (rather than deleted) so those 106 call sites
        * keep working untouched, and so any component that adds one in future is
        * harmless either way.
        *
        * The old implementations also had three bugs worth recording: formatName
        * masked only the SURNAME (real first names leaked), formatEmail was never
        * called from anywhere in the codebase, and formatAge was skipped at
        * AdminTable.vue:166 so ages leaked in the Overdue Recall table. All three
        * are fixed by masking at the data layer instead.
        */
       /** Header toggle target — Super Admin only (see Header.vue for the gate). */
       setPhiMask: function(on) { setUserMask(on); },

       formatName: function(name) { return name; },
       formatPhone: function(phone) { return phone; },
       formatEmail: function(email) { return email; },
       formatBirthDate: function(bdate) { return bdate; },
       formatSubscriberID: function(subID) { return subID; },

       /* Age is deliberately NOT masked: it is left real so "(57)" reads
        * naturally on camera, and an age under 90 is not a Safe Harbor
        * identifier on its own. DOB is faked instead (phiMask.js::shiftDob). */
       formatAge: function(age) { return age; },

       formatCommLogDateTime(d) {
        let a = moment.utc(d).tz(this.allSettings.GENERAL_TIMEZONE.PropertyValue).format('MM/DD/YYYY hh:mm a');
        let b = moment(d).tz(this.allSettings.GENERAL_TIMEZONE.PropertyValue).format('MM/DD/YYYY hh:mm a');
        let c = moment(d).format('MM/DD/YYYY hh:mm a');  
        let e = moment.utc(d).format('MM/DD/YYYY hh:mm a');
        //console.log(`a: ${a}, b: ${b}, c: ${c}, e: ${e}`);
        let ret = b;
        return ret;
       },

      chooseNone(selectedPatients) {
        //selectedPatients = [];
        while(selectedPatients.length > 0) {selectedPatients.pop();}
      },
  
      chooseAll(data, selectedPatients){      
        // Clear existing selection first to prevent duplicates
        selectedPatients.splice(0, selectedPatients.length);
        // Add all patients
        data.forEach(p => selectedPatients.push(p));
      },
  
      handleMainCheckbox(selectedPatients) {
        this.isMainChecked ? this.chooseAll(selectedPatients) : this.chooseNone(selectedPatients);
      },

       // This is used for showing badges for upcoming follow-ups
       taskTypeColorClass(taskTypeID) {
         let ret;
         switch(taskTypeID) {
           case 3: ret = 'badge badge-warning'; break;
           case 4: ret = 'badge badge-dark'; break;
           case 8: ret = 'badge badge-secondary'; break;
           default: ret = 'badge badge-danger'; break;
         }
         return ret;
       },

       removeSelectedPatient(patients, patNum) {
         let removeIdx = patients.findIndex(each => {
           return each.PatNum === patNum;
         });
         patients.splice(removeIdx, 1);
       },

       isSelectedPatient(patients, id) {
         if(patients) {
          let idx = patients.findIndex(each => {
              return each.PatNum === id;
          });
          return idx !== -1;
         } else
          return false;
      },
 
      updateSelectedPatients(patients, row) {
         let idx = patients.findIndex(each => {
            return each.PatNum === row.PatNum;
         });
         if(idx === -1)
            patients.push(row);
         else 
            patients.splice(idx, 1);
      },

      createNewList(patients) {
         if (this.somePatientsBelongsToList) {           
           this.$toast.error('One or more patients you selected below already belong to a list. You can not add them to another list');
         }
         else if (patients.length === 0){
           this.$toast.error("No patients selected");           
         }
         else {
           // clear errors if any before opening the pop-up
           this.resetError();
           this.$bvModal.show('create-list');
         }
       },

       addToExistingList(patients) {
        if (this.somePatientsBelongsToList) {          
          this.$toast.error('One or more patients you selected below already belong to a list. You can not add them to another list');
        }
        else if (patients.length === 0){
          this.$toast.error('No Patients selected');          
        }
        else {
          // clear errors if any before opening the pop-up
          this.resetError();
          this.$bvModal.show('add-to-existing-list');
        }
       },

       resetError() {
        this.setErrored(false);
        this.setMessage('');
       },

       resetError2() {
        this.setErrored2(false);
        this.setMessage2('');
       },

       resetAllErrors() {
        this.resetError();
        this.resetError2();
       },

       getDetailPageName(taskTypeID) {
        let detailPageName;
        switch(taskTypeID) {
          case 2: detailPageName = 'AgingDetails'; break;
          case 3: detailPageName = 'OverdueRecallDetails'; break;
          case 4: detailPageName = 'unscheduledPatientDetails'; break;
          case 5: detailPageName = 'OutstandingPreAuthDetails'; break;
          case 6: detailPageName = 'ProcessedPreAuthDetails'; break;
          case 7: detailPageName = 'PatientBalancesDetails'; break;
          case 8: detailPageName = 'BrokenAppointmentDetails'; break;
          default: break;
        }
        return detailPageName;
      },

       getExtraParams(taskType) {
         switch(taskType) {
           case 'overdue-recall': 
                                   return {
                                     detailPageName: 'OverdueRecallDetails',
                                   };
                                   break;
           case 'unscheduled-treatment': 
                                   return {
                                     detailPageName: 'unscheduledPatientDetails',
                                   };
                                   break;
   
           case 'overdue-claims': 
                                   return {
                                     detailPageName: 'OverdueRecallDetails',
                                   };
                                   break;

            case 'outstanding-claims':
                                    return {
                                      detailPageName: 'AgingDetails',
                                    };
                                    break;
   
           case 'outstanding-preauth':
                                   return {
                                     detailPageName: 'OutstandingPreAuthDetails',
                                   };
                                   break;

            case 'processed-preauths':
                                    return {
                                      detailPageName: 'ProcessedPreAuthDetails',
                                    };
                                    break;
   
           case 'post-op-calls': 
                                   return {
                                     detailPageName: 'PostOpCallDetails',
                                   };
                                   break;   
           
           case 'broken-appointments':
                                   return {
                                     detailPageName: 'BrokenAppointmentDetails',
                                   };
                                   break;
            case 'patient-balances':
                                    return {
                                      detailPageName: 'PatientBalancesDetails',
                                    };
                                    break;

            case 'asap-list':
                                      return {
                                        detailPageName: 'AsapList',
                                      };
                                      break;
         }
       },

       getEmployeeName(employeeID) {
        if(this.employees) {
          let users = this.employees.filter(e => e.EmployeeID === employeeID);
          if(users.length > 0)
            return users[0].Name;
          else
            return 'N/A';
        } else
          return 'N/A';
      },

      //For canada only (for now)
      convertUniversalToFDI(universalNumber) {

        if (!universalNumber){
          return '';
        }
        else {
          // Mapping of Universal to FDI for permanent teeth (1-32)
          
            // Mapping of Universal to FDI for primary teeth (A-T)
            const universalPrimaryToFDI = {
              1: 18,  2: 17,  3: 16,  4: 15,  5: 14,  6: 13,  7: 12,  8: 11,
                9: 21, 10: 22, 11: 23, 12: 24, 13: 25, 14: 26, 15: 27, 16: 28,
                17: 48, 18: 47, 19: 46, 20: 45, 21: 44, 22: 43, 23: 42, 24: 41,
                25: 31, 26: 32, 27: 33, 28: 34, 29: 35, 30: 36, 31: 37, 32: 38,
                'A': 55, 'B': 54, 'C': 53, 'D': 52, 'E': 51,
                'F': 61, 'G': 62, 'H': 63, 'I': 64, 'J': 65,
                'K': 75, 'L': 74, 'M': 73, 'N': 72, 'O': 71,
                'P': 81, 'Q': 82, 'R': 83, 'S': 84, 'T': 85
            };
            return universalPrimaryToFDI[universalNumber.toUpperCase()] || 'Invalid tooth letter';
            
        }
    },

      getProviderName(provNum) {
        if(!isNaN(provNum))
          provNum = parseInt(provNum, 10);
        let provider = this.providers.find(p => p.ProvNum === provNum);
        if(provider) {
          return `${provider.FName} ${provider.LName}`;
        } else {
          return "N/A";
        }
      },

      sumArrValue(arr, property) {
        if(arr && arr.length > 0)
          return arr.reduce((a, b) => a + (b[property] || 0), 0);
        else
          return 0;
      },

      isObjectEmpty(obj) {
        return obj && Object.keys(obj).length === 0;
      },

      arraysEqual(a1,a2) {
        /* WARNING: arrays must not contain {objects} or behavior may be undefined */
       return JSON.stringify(a1)==JSON.stringify(a2);
      },

      showModal(name) {            
        this.$refs[name].show();
      },

      hideModal(ref) {
          this.$refs[ref].hide()
      },
    },

    computed: {      
      ...mapGetters({
        'customer' : 'customer/employeeName',
        'getSite': 'customer/getSite',
        'activeEmployees': 'customer/activeEmployees',   
        'getProviderFromProvNum': 'customer/getProviderFromProvNum'
      }),
    
      ...mapState({
         loading: state => state.alert.loading,
         loading2: state => state.alert.loading2,
         loaded: state => state.alert.loaded,
         loaded2: state => state.alert.loaded2,
         errored: state => state.alert.errored,
         alertMessage: state => state.alert.message,
         dismissSuccessCountDown : state => state.alert.dismissSuccessCountDown, 
         queryResults : state => state.teamio.queryResults,
         custom : state => state.teamio.custom,
         siteID: state => state.customer.siteID,
         user: state => state.customer.user,
         customerID: state => state.customer.customerID,
         userID: state => state.customer.userID,
         sites: state => state.customer.sites,
         employees: state => state.customer.employees,
         settings: state => state.customer.settings,
         rules: state => state.customer.rules,
         providers: state => state.customer.providers,
         workflowTriggers: state => state.customer.workflowTriggers,
         tasksLoaded: state => state.tasks.tasksLoaded,  
         privacyOn: state => state.customer.privacyOn
      }),

      isSuperAdmin() {
         return !!(this.user && this.user.Role === 'Super Admin');
      },

      allSettings() {
         if(this.settings)
            return _.keyBy(this.settings, 'Property');
      },

      /**
       * Per-site Tx Presenter access flag. Matches the FORMS_PAID_FOR /
       * WORKFLOW_ACTIVATED string-boolean convention: `PropertyValue` is the
       * string 'TRUE' or 'FALSE' in `teamio.settings`. Missing row = disabled.
       * Toggle via Gautam → SuperAdmin → Features tab.
       */
      txPresenterEnabled() {
         return !!(this.allSettings
            && this.allSettings.TX_PRESENTER_ENABLED
            && this.allSettings.TX_PRESENTER_ENABLED.PropertyValue === 'TRUE');
      },

      /**
       * Composite gate that replaced the bare `isSuperAdmin` check on every
       * Tx Presenter surface (sidebar entry, Patient Profile "Tx Plans"
       * button, route guards). Super Admin always has access (safety net so
       * we never lock ourselves out); per-site flag opens the feature to
       * trial-office TCs without granting them the broader Super Admin role.
       */
      canAccessTxPresenter() {
         return this.isSuperAdmin || this.txPresenterEnabled;
      },

      /**
       * Per-site Insurance Verification access flag. Same string-boolean
       * convention as TX_PRESENTER_ENABLED: `PropertyValue` is 'TRUE' / 'FALSE'
       * in `teamio.settings`; missing row = disabled. Toggled via Gautam →
       * SuperAdmin → Features (Slice 6c onboarding).
       */
      insuranceVerificationEnabled() {
         return !!(this.allSettings
            && this.allSettings.INSURANCE_VERIFICATION_ENABLED
            && this.allSettings.INSURANCE_VERIFICATION_ENABLED.PropertyValue === 'TRUE');
      },

      /**
       * Access gate for the PatientProfile Insurance Verification chip. Super Admin
       * always has access (safety net, mirrors canAccessTxPresenter); per-site flag
       * opens it to a practice's users.
       *
       * ⚠ The SIDEBAR no longer uses this, and the two must NOT be re-unified.
       * topmixin's `showInsuranceVerificationNav` is flag-ONLY on purpose: the nav
       * icon leads to /dashboard, which is flag-GATED and answers 403, and Lumigo
       * pages that 403 to Slack. Keeping the Super-Admin bypass here is safe only
       * because this gate leads to `/patient-status`, which is SOFT-gated — it
       * answers 200 `{enabled:false}` when the flag is off, so it never 403s and
       * never alerts. Same feature, two endpoints, two different refusal styles.
       */
      canAccessInsuranceVerification() {
         return this.isSuperAdmin || this.insuranceVerificationEnabled;
      },

      // AI form builder ("Create with AI" / "Edit with AI") — optional paid add-on,
      // gated per-site by the AI_FORMS_PAID_FOR setting (mirrors AI_FOLLOWUP_PAID_FOR).
      // Toggled from the SuperAdmin Features tab. When off, the AI buttons show disabled "Coming soon".
      aiFormsEnabled() {
         return !!(this.allSettings
            && this.allSettings.AI_FORMS_PAID_FOR
            && this.allSettings.AI_FORMS_PAID_FOR.PropertyValue === 'TRUE');
      },
      // AI features aren't launched (2026-09-16): an unentitled site sees the AI buttons disabled with
      // "Coming soon". Waits for settings so a paying site never flashes the placeholder.
      aiFormsComingSoon() {
         return !!(this.allSettings && Object.keys(this.allSettings).length) && !this.aiFormsEnabled;
      },

      /**
       * Patient Profile photo capture (tap the header avatar → camera → save).
       *
       * DERIVED, not stored: the feature ships with the "All-in-One" bundle, and
       * All-in-One is exactly Forms + Appointment Reminders. Adding a third
       * PATIENT_PHOTO_PAID_FOR row would mean every All-in-One site already on
       * the platform silently loses the feature until someone back-fills it, and
       * every future bundle sale needs two toggles flipped instead of one. So the
       * rule reads the two flags that already exist, and there is deliberately no
       * SuperAdmin toggle / seeder default of its own to keep in sync.
       *
       * Same string-boolean convention as the rest of teamio.settings
       * ('TRUE'/'FALSE'); missing row = off, and the compare is trimmed +
       * upper-cased because these two are old rows written by several different
       * onboarding paths.
       */
      patientProfilePhotoEnabled() {
         // The rule lives in utils/patientPhotoGate.js so Header.vue (no mixin) reads the same one.
         return isPatientPhotoEnabled(this.allSettings);
      },

      /**
       * True when patient data on screen is faked — by EITHER the per-user
       * header toggle (localStorage, Super Admin only, the one to use for
       * recording) OR the site-wide GENERAL_DEMO_MODE_ON setting.
       *
       * Reads the reactive phiMaskState rather than allSettings directly, so a
       * component gating UI on this stays in sync with what the axios
       * interceptor is actually doing. Note this no longer drives any masking
       * itself — the six formatX helpers above are pass-throughs now.
       */
      isDemoModeOn() {
         const s = maskState();
         return s.userMask === true || s.siteMask === true;
      },

      isStatusLocked() {
        return this.allSettings && this.allSettings.GENERAL_STATUS_LOCKED && this.allSettings.GENERAL_STATUS_LOCKED.PropertyValue.toUpperCase() === 'TRUE' ? true : false;
      },

      isMainChecked() {
        if(this.data && this.selectedPatients)
          return this.data.length === this.selectedPatients.length;
      },

      getCountry(){
        //should never really have a value of "none", but just having it there as a placholder
        return this.allSettings && this.allSettings.GENERAL_COUNTRY && this.allSettings.GENERAL_COUNTRY.PropertyValue ? this.allSettings.GENERAL_COUNTRY.PropertyValue.toUpperCase() : 'USA';
      },

      patNum() {
        if(this.patientID && this.patientID !== null) {
          return this.patientID.substring(this.patientID.lastIndexOf('-')+1);
        } else if (this.teamioPatientID && this.teamioPatientID !== null) {
          return this.teamioPatientID.substring(this.teamioPatientID.lastIndexOf('-')+1);
        } else if(this.$attrs['patNum']) {
          return this.$attrs['patNum'];
        } else
          return null;
      },

      dateFormatStr() {
        let _default = 'MM/DD/YYYY';
        let site = this.getSite(this.siteID);
        if(site) {
          return site.DateFormat || _default;
        } else
          return _default;        
      },

      isAdminSite() {
        return ['4dd2fc2c-d7cb-11e9-bac0-0ad6daef1536', '437dd9f9-7850-11ea-ac82-160d1264b43d'].includes(this.siteID);
        //return ['437dd9f9-7850-11ea-ac82-160d1264b43d'].includes(this.siteID);
      },

      isDev() {
        return process.env.VUE_APP_NODE_ENV === 'development';
      }

    }

}